import { Link as RouterLink } from "@tanstack/react-router";
import {
  Building2,
  CreditCard,
  ExternalLink,
  User as UserIcon,
} from "lucide-react";

import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { ItsmDataSection } from "@/components/bootstrap/itsm-data-section";
import { IntegrationConfigSection } from "@/components/tenants/integration-config-section";
import { TrialStatusDot } from "@/components/tenants/trial-status-dot";
import type { TenantRead, TenantTrialStatus } from "@harmonyso/tenants-sdk";
import type { TenantBillingConfigResponse } from "@harmonyso/billing-api-sdk";
import { isRemoteCellId } from "@/lib/cells/cells";
import {
  describeTrial,
  formatTrialDate,
  getTrialIndicatorFromStatus,
} from "@/lib/trial";

interface TenantInfoCardsProps {
  tenant: TenantRead;
  tenantId?: string;
  billingConfig?: TenantBillingConfigResponse;
  billingConfigLoading?: boolean;
  billingConfigError?: unknown;
  trial?: TenantTrialStatus | null;
  trialLoading?: boolean;
}

function formatBillingLabel(value?: string | null) {
  if (!value) {
    return "—";
  }

  return value
    .replace(/_/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function formatContractPeriod(config?: TenantBillingConfigResponse) {
  if (!config) {
    return "Not configured";
  }

  const startDate = new Date(config.contract_start_date).toLocaleDateString();
  const endDate = new Date(config.contract_end_date).toLocaleDateString();

  return `${startDate} → ${endDate}`;
}

function isNotFoundError(error: unknown) {
  return (
    (error as { response?: { status?: number } } | null)?.response?.status ===
    404
  );
}

export function TenantInfoCards({
  tenant,
  tenantId,
  billingConfig,
  billingConfigLoading,
  billingConfigError,
  trial,
  trialLoading,
}: TenantInfoCardsProps) {
  const billingConfigMissing = isNotFoundError(billingConfigError);
  const tierLabel = billingConfigLoading
    ? "Loading"
    : billingConfig?.tier_name ||
      (billingConfigMissing ? "No config" : "Unavailable");

  return (
    <>
      <div className="grid gap-6 md:grid-cols-2">
        {/* Basic Information */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5" />
              Basic Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-3">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Tenant ID</span>
                <span className="text-muted-foreground font-mono text-sm">
                  {tenant.id}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Company Name</span>
                <span className="text-muted-foreground text-sm">
                  {tenant.company}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Subdomain</span>
                <span className="text-muted-foreground font-mono text-sm">
                  {tenant.subdomain}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">URL</span>
                <a
                  href={`https://${tenant.subdomain}.${import.meta.env.VITE_HARMONY_URL}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-mono text-sm text-blue-600 hover:text-blue-800 hover:underline dark:text-blue-400 dark:hover:text-blue-300"
                >
                  https://{tenant.subdomain}.{import.meta.env.VITE_HARMONY_URL}
                </a>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Trial</span>
                <span className="text-muted-foreground text-right text-sm">
                  <span className="inline-flex items-center gap-2">
                    {!trialLoading && (
                      <TrialStatusDot
                        indicator={getTrialIndicatorFromStatus(trial)}
                      />
                    )}
                    {trialLoading ? "Loading" : describeTrial(trial)}
                  </span>
                  {trial && (
                    <span className="block text-xs">
                      {formatTrialDate(trial.start_date)} →{" "}
                      {formatTrialDate(trial.end_date)}
                    </span>
                  )}
                </span>
              </div>
              {tenant.industry && (
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Industry</span>
                  <span className="text-muted-foreground text-sm">
                    {tenant.industry.charAt(0).toUpperCase() +
                      tenant.industry.slice(1)}
                  </span>
                </div>
              )}
              {tenant.size && (
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Company Size</span>
                  <span className="text-muted-foreground flex items-center gap-1 text-sm">
                    <UserIcon className="h-3 w-3" />
                    {tenant.size}
                  </span>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <div className="flex flex-col gap-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="h-5 w-5" />
                Billing & Settings
              </CardTitle>
              {/* Billing is served by the primary cell only; opening it for
                  another cell's tenant would create that tenant's config there. */}
              {!isRemoteCellId(tenant.deployment_cell) && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <RouterLink
                      to="/billing"
                      search={{ tenantId: tenant.id, tab: "config" }}
                      aria-label="Open billing"
                      className="hover:bg-accent hover:text-accent-foreground inline-flex h-8 w-8 items-center justify-center rounded-md border transition-colors"
                    >
                      <ExternalLink className="h-4 w-4" />
                    </RouterLink>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Open billing</p>
                  </TooltipContent>
                </Tooltip>
              )}
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Tier</span>
                  <Badge
                    variant={billingConfig ? "default" : "secondary"}
                    className={`text-xs ${billingConfig ? "bg-blue-600 text-white" : ""}`}
                  >
                    {tierLabel}
                  </Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Billing Model</span>
                  <span className="text-muted-foreground text-sm">
                    {billingConfig
                      ? formatBillingLabel(billingConfig.billing_model)
                      : "Not configured"}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Contract Period</span>
                  <span className="text-muted-foreground text-sm">
                    {formatContractPeriod(billingConfig)}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>

          {tenantId && <ItsmDataSection tenantId={tenantId} />}
          {tenantId && <IntegrationConfigSection tenantId={tenantId} />}
        </div>
      </div>
    </>
  );
}
